import type { SheetRow, SheetColumn } from "../../engine/models";
import { Theme } from "@mui/material";

// ── Types ──────────────────────────────────────────────────────────────────

interface StickyPalette {
    section: { color: string; background: string };
    category: { color: string; background: string };
    line: { color: string; background: string };
    disabled: { color: string; background: string };
}

// ── Palettes ───────────────────────────────────────────────────────────────

const LIGHT_STICKY_PALETTES: StickyPalette[] = [
    {
        section: { color: "#225982", background: "#94D1FF" },
        category: { color: "#225982", background: "#C8E7FE" },
        line: { color: "#2C8FDA", background: "#EEF5FF" },
        disabled: { color: "#9E9E9E", background: "#F2F2F2" },
    },
    {
        section: { color: "#724707", background: "#FFDAA2" },
        category: { color: "#955D09", background: "#FCEACE" },
        line: { color: "#CB7F0E", background: "#FAF7F2" },
        disabled: { color: "#9E9E9E", background: "#F2F2F2" },
    },
    {
        section: { color: "#055832", background: "#95E7C1" },
        category: { color: "#055832", background: "#BBEFD7" },
        line: { color: "#148451", background: "#E8FCF3" },
        disabled: { color: "#9E9E9E", background: "#F2F2F2" },
    },
];

const DARK_STICKY_PALETTES: StickyPalette[] = [
    {
        section: { color: "#D5EDFF", background: "#0082E4" },
        category: { color: "#D5EDFF", background: "#015A9D" },
        line: { color: "#D5EDFF", background: "#002E50" },
        disabled: { color: "#9E9E9E", background: "#F2F2F222" },
    },
    {
        section: { color: "#FFECD0", background: "#D08D28" },
        category: { color: "#FFECD0", background: "#9F6A1C" },
        line: { color: "#FFECD0", background: "#533911" },
        disabled: { color: "#9E9E9E", background: "#F2F2F222" },
    },
    {
        section: { color: "#DAEEE5", background: "#21A468" },
        category: { color: "#DAEEE5", background: "#1A754C" },
        line: { color: "#DAEEE5", background: "#0C4129" },
        disabled: { color: "#9E9E9E", background: "#F2F2F222" },
    },
];

// ── Section Cell Borders ───────────────────────────────────────────────────

/**
 * Builds the border/rounding style for a section header cell.
 *
 * The original bidStore.jsx logic:
 * - Expanded: side borders show, top border shows, bottom border shows.
 *   First/last columns get top-left/top-right rounding.
 * - Collapsed: first column gets both left roundings, last column gets both right roundings.
 *   No side/bottom borders (section is a standalone rounded pill).
 */
const buildSectionBorderStyle = (
    isFirstCol: boolean,
    isLastCol: boolean,
    isExpanded: boolean,
    theme: Theme,
): Record<string, any> => {
    const paper = theme.palette.background.paper;
    const borderColor = theme.palette.text.disabled;

    const style: any = {
        background: paper,
        border: {
            top: { color: borderColor, style: "solid", width: "1px" },
            bottom: { color: isExpanded ? borderColor : paper },
            left: { color: paper },
            right: { color: paper },
        },
        fontWeight: 600,
    };

    if (!isExpanded) {
        style.border.top.color = "transparent";
    }

    if (isFirstCol) {
        style.borderTopLeftRadius = "8px";
        if (isExpanded) {
            style.border.left.color = borderColor;
        } else {
            style.borderBottomLeftRadius = "8px";
        }
    } else if (isLastCol) {
        style.borderTopRightRadius = "8px";
        if (isExpanded) {
            style.border.right.color = borderColor;
        } else {
            style.borderBottomRightRadius = "8px";
        }
    }

    return style;
};

// ── Sticky Column Detection ────────────────────────────────────────────────

/** The 3 rightmost sticky columns that receive palette coloring. */
const STICKY_COLUMN_IDS = new Set([
    "client_budget",
    "client_budget_buffered",
    "client_quoted",
    "client_quoted_original",
    "variance",
    "client_budget_buffered_original",
]);

const isStickyColumn = (colId: string): boolean => {
    const baseId = colId.includes(".") ? colId.split(".").pop()! : colId;
    return STICKY_COLUMN_IDS.has(baseId);
};

// ── Main Styling Function ──────────────────────────────────────────────────

export const applySpreadsheetStyles = (
    rows: SheetRow[],
    columns: SheetColumn[],
    theme: Theme,
    readOnly: boolean,
    isRunningMode: boolean,
    expansions: Record<string, boolean>,
): SheetRow[] => {
    const styledRows = rows.map((r) => ({
        ...r,
        cells: r.cells.map((c) => ({ ...c })),
    }));
    const isDarkMode = theme.palette.mode === "dark";
    const palettes = isDarkMode ? DARK_STICKY_PALETTES : LIGHT_STICKY_PALETTES;
    const paperBg = theme.palette.background.paper;
    const defaultBg = theme.palette.background.default;

    let currentSection: string | null = null;

    for (let i = 0; i < styledRows.length; i++) {
        const row = styledRows[i];
        const isFirstRow = i === 0;
        const isLastRow = i === styledRows.length - 1;
        const rowIdStr = row.rowId.toString();

        const isSection = rowIdStr.startsWith("group-") && !row.parentId;
        const isCategory = rowIdStr.startsWith("group-") && !!row.parentId;
        const isAddButton = rowIdStr.startsWith("action-") && !isLastRow;
        const isSpacer = rowIdStr.startsWith("spacer-");

        if (isSection) {
            currentSection = rowIdStr;
        } else if (isSpacer) {
            currentSection = null;
        }

        const isSectionLastRow =
            isAddButton && row.parentId?.toString() === currentSection;
        const isExpanded = currentSection
            ? !!expansions[currentSection]
            : false;

        // Pop from a copy of palettes to cycle through colors for sticky columns
        const stickyPalettesCycle = [...palettes];

        for (let j = row.cells.length - 1; j >= 0; j--) {
            const cell = row.cells[j];
            const isFirstCol = j === 0;
            const isLastCol = j === row.cells.length - 1;
            const colId = (columns[j]?.columnId as string) || "";
            const isSticky = isStickyColumn(colId);
            const isRunningCustomId = colId.includes("custom_id");

            if (!cell.style) {
                cell.style = {};
            }

            // ── Base border styling ──────────────────────────────────────

            if (isFirstRow) {
                cell.style = {
                    ...cell.style,
                    color: theme.palette.text.secondary,
                    background: defaultBg,
                    border: {
                        top: { color: "transparent" },
                        bottom: { color: paperBg },
                        left: { color: paperBg },
                        right: { color: paperBg },
                    },
                };
            } else if (isLastRow) {
                cell.style = {
                    ...cell.style,
                    background: defaultBg,
                    boxShadow: "none",
                    border: {
                        top: { color: defaultBg },
                        bottom: { color: defaultBg },
                        left: { color: defaultBg },
                        right: { color: defaultBg },
                    },
                };
            } else if (isSpacer) {
                // Spacer rows: match page background so pane background never bleeds through
                cell.style = {
                    ...cell.style,
                    background: defaultBg,
                    border: {
                        top: { color: defaultBg },
                        bottom: { color: defaultBg },
                        left: { color: defaultBg },
                        right: { color: defaultBg },
                    },
                };
            } else if (isAddButton) {
                cell.style = {
                    ...cell.style,
                    color: theme.palette.text.primary,
                    background: defaultBg,
                    border: {
                        top: { color: paperBg },
                        bottom: { color: paperBg },
                        left: { color: "transparent" },
                        right: { color: "transparent" },
                    },
                };
            } else {
                // Default data cells — explicit background + text color for dark mode support
                cell.style = {
                    ...cell.style,
                    color: theme.palette.text.primary,
                    background: defaultBg,
                    boxShadow: "none",
                    border: {
                        top: { color: paperBg },
                        bottom: { color: paperBg },
                        left: { color: paperBg },
                        right: { color: paperBg },
                    },
                };
            }

            // ── Side borders for expanded sections ───────────────────────

            if (isFirstCol && !isFirstRow && !isLastRow && !isSpacer) {
                cell.style = {
                    ...cell.style,
                    justifyContent: "center",
                    border: {
                        ...cell.style.border,
                        left: {
                            color: isExpanded
                                ? theme.palette.text.disabled
                                : "transparent",
                        },
                    },
                };
            } else if (isLastCol && !isFirstRow && !isLastRow && !isSpacer) {
                cell.style = {
                    ...cell.style,
                    border: {
                        ...cell.style.border,
                        right: {
                            color: isExpanded
                                ? theme.palette.text.disabled
                                : "transparent",
                        },
                    },
                };
            }

            // ── Section header treatment ─────────────────────────────────

            if (isSection) {
                const sectionStyle = buildSectionBorderStyle(
                    isFirstCol,
                    isLastCol,
                    isExpanded,
                    theme,
                );
                cell.style = { ...cell.style, ...sectionStyle };
            } else if (isSectionLastRow) {
                // Bottom border + rounding for the last row of an expanded section
                cell.style = {
                    ...cell.style,
                    border: {
                        ...cell.style.border,
                        bottom: { color: theme.palette.text.disabled },
                    },
                };
                if (isFirstCol) cell.style.borderBottomLeftRadius = "8px";
                else if (isLastCol) cell.style.borderBottomRightRadius = "8px";
            }

            // ── Sticky column palette coloring ───────────────────────────

            const stickyPalette = stickyPalettesCycle.pop() || palettes[0];
            if (isSticky && !isSpacer && !isLastRow && !isFirstRow) {
                const isCellRunning =
                    isRunningMode || colId.startsWith("line_item_running.");
                const cellPalette =
                    readOnly && !isCellRunning
                        ? stickyPalette.disabled
                        : isSection
                          ? stickyPalette.section
                          : isCategory
                            ? stickyPalette.category
                            : stickyPalette.line;
                cell.style = { ...cell.style, ...cellPalette };
            } else {
                // Non-sticky + non-editable → dim text
                if (readOnly || isRunningCustomId) {
                    const isCellRunning =
                        isRunningMode || colId.startsWith("line_item_running.");
                    cell.nonEditable =
                        !isCellRunning || cell.nonEditable || isRunningCustomId;
                }
                if (
                    cell.nonEditable &&
                    !isSection &&
                    !isSpacer &&
                    !isFirstRow &&
                    !isLastRow
                ) {
                    cell.style.color = isDarkMode ? "#9E9E9E88" : "#9E9E9E";
                }
            }

            // ── Special column treatments ────────────────────────────────

            if (colId.includes("variance") && (cell as any).value < 0) {
                cell.style.color = theme.palette.error.main;
            }
            if (
                readOnly &&
                (colId.includes("client_budget_buffered_original") ||
                    colId.includes("client_quoted_original"))
            ) {
                cell.style.color = isDarkMode ? "#FFFFFF66" : "#00000055";
            }
        }
    }

    return styledRows;
};
